export interface InlineEditableBatchItem {
  id?: number
  name: string
}

export interface InlineEditableBatchChange {
  id: number
  name: string
}

export interface UseInlineEditableBatchListOptions {
  create: (name: string) => Promise<unknown>
  createEmptyItem?: () => InlineEditableBatchItem
  remove: (id: number) => Promise<unknown>
  update: (item: InlineEditableBatchChange) => Promise<unknown>
}

const cloneItem = (item: InlineEditableBatchItem): InlineEditableBatchItem => ({ ...item })

const isExistingItem = (item: InlineEditableBatchItem): item is InlineEditableBatchChange => item.id !== undefined

// Управляет одним редактируемым inline-списком сущностей вида { id?, name }:
// локальный draft, диф относительно исходного состояния и batch-сохранение.
// Оркестрацию загрузки/нотификаций/refresh оставляем вызывающей модели.
export const useInlineEditableBatchList = (options: UseInlineEditableBatchListOptions) => {
  const createEmptyItem = options.createEmptyItem ?? (() => ({ name: '' }))

  const items = ref<InlineEditableBatchItem[]>([])
  const baseItems = ref<InlineEditableBatchItem[]>([])
  const itemsToDelete = ref<number[]>([])

  const setItems = (list: InlineEditableBatchItem[]) => {
    items.value = list.map(cloneItem)
    baseItems.value = list.map(cloneItem)
    itemsToDelete.value = []
  }

  const newItems = computed(() => items.value.filter((item) => item.id === undefined && item.name !== ''))

  const changedItems = computed(() => {
    const existing = items.value.filter(isExistingItem)

    return baseItems.value.reduce<InlineEditableBatchChange[]>((changed, base) => {
      const match = existing.find((item) => item.id === base.id && item.name !== base.name)

      if (match) {
        changed.push(match)
      }

      return changed
    }, [])
  })

  const isDirty = computed(() => JSON.stringify(items.value) !== JSON.stringify(baseItems.value))

  const hasBatchChanges = computed(() =>
    Boolean(newItems.value.length || changedItems.value.length || itemsToDelete.value.length),
  )

  const addItem = (item: InlineEditableBatchItem = createEmptyItem()) => {
    items.value.push(item)
  }

  const removeItem = (index: number) => {
    const [removed] = items.value.splice(index, 1)

    if (removed?.id !== undefined) {
      itemsToDelete.value.push(removed.id)
    }
  }

  const reset = () => {
    items.value = baseItems.value.map(cloneItem)
    itemsToDelete.value = []
  }

  const commit = async () => {
    await Promise.all(newItems.value.map((item) => options.create(item.name)))
    await Promise.all(itemsToDelete.value.map((id) => options.remove(id)))
    await Promise.all(changedItems.value.map((item) => options.update(item)))

    itemsToDelete.value = []
  }

  return {
    items,
    baseItems,
    itemsToDelete,
    newItems,
    changedItems,
    isDirty,
    hasBatchChanges,
    setItems,
    addItem,
    removeItem,
    reset,
    commit,
  }
}
